import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import React, { forwardRef } from 'react';
import { cn } from '../lib/cn';

/**
 * shadcn-style Button driven by `class-variance-authority`. Variants and
 * sizes mirror the Old TMS app so look-and-feel is preserved across the suite.
 *
 * The icon props (`leftIcon` / `rightIcon` / `icon`) are kept from the Old
 * Button so callers don't have to inline lucide elements alongside text.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1 rounded-md font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black/50 disabled:opacity-60 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-blue-600 text-white hover:bg-blue-700',
        secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300',
        ghost: 'bg-transparent text-gray-800 hover:bg-gray-100',
        outline: 'border border-gray-300 text-gray-900 hover:bg-gray-100',
        danger: 'bg-red-600 text-white hover:bg-red-700',
        link: 'h-auto bg-transparent p-0 text-blue-600 hover:underline',
        custom: 'bg-transparent hover:bg-gray-100',
      },
      size: {
        xs: 'h-6 px-1 text-xs',
        sm: 'h-9 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-5 text-base',
      },
      iconOnly: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      { iconOnly: true, size: 'xs', class: 'h-7 w-7 px-0' },
      { iconOnly: true, size: 'sm', class: 'h-9 w-9 px-0' },
      { iconOnly: true, size: 'md', class: 'h-10 w-10 px-0' },
      { iconOnly: true, size: 'lg', class: 'h-12 w-12 px-0' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'sm',
      iconOnly: false,
    },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, 'iconOnly'> {
  /** Optional left icon component from lucide-react */
  leftIcon?: LucideIcon;
  /** Optional right icon component from lucide-react */
  rightIcon?: LucideIcon;
  /** If provided and no children, renders an icon-only button */
  icon?: LucideIcon;
  /** Background class for the icon container, e.g. 'bg-blue-100' */
  iconBgClass?: string;
  /** Color class for the icon itself, e.g. 'text-blue-600' */
  iconColorClass?: string;
  /** Show a loading spinner and disable interactions */
  loading?: boolean;
  /** When icon-only, you should provide an accessible label */
  ariaLabel?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      className,
      children,
      variant,
      size,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      icon: SoloIcon,
      iconBgClass,
      iconColorClass,
      loading = false,
      disabled,
      ariaLabel,
      ...props
    },
    ref,
  ) => {
    const isIconOnly = !!SoloIcon && !children;
    const computedDisabled = disabled || loading;

    const classes = cn(buttonVariants({ variant, size, iconOnly: isIconOnly }), className);

    const iconClass = cn('h-5 w-5', iconBgClass, iconColorClass);

    return (
      <button
        ref={ref}
        type={type}
        aria-label={isIconOnly ? ariaLabel : undefined}
        className={classes}
        disabled={computedDisabled}
        {...props}
      >
        {/* Loading spinner takes precedence */}
        {loading && (
          <svg
            className={cn(iconClass, 'animate-spin')}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        )}

        {!loading && isIconOnly && SoloIcon && <SoloIcon className={iconClass} />}

        {!loading && !isIconOnly && LeftIcon && <LeftIcon className={iconClass} />}

        {!isIconOnly && children}

        {!loading && !isIconOnly && RightIcon && <RightIcon className={iconClass} />}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { buttonVariants };
